import { DragCancelEvent, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import { message } from "antd";
import type {
  BusinessResponse,
  Category,
  ContentTopic,
} from "api/config/chalice-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch } from "store";
import { updateBusinessDetails } from "store/business/businessActions";

interface UseTopicDragAndDropProps {
  topics: ContentTopic[];
  categories: Category[];
  sortedCategoryNames: string[];
  categoryIdToNameMap: Record<string, string>;
  currentBusiness: BusinessResponse | null;
}

export const useTopicDragAndDrop = ({
  topics,
  categories: _categories,
  sortedCategoryNames: _sortedCategoryNames,
  categoryIdToNameMap: _categoryIdToNameMap,
  currentBusiness,
}: UseTopicDragAndDropProps) => {
  const dispatch = useAppDispatch();
  const [activeItem, setActiveItem] = useState<ContentTopic | null>(null);

  const topicsRef = useRef(topics);

  useEffect(() => {
    topicsRef.current = topics;
  }, [topics]);

  const [activeCategoryKeys, setActiveCategoryKeys] = useState<string[]>([]);

  const handleCollapseChange = useCallback((keys: string | string[]) => {
    setActiveCategoryKeys(Array.isArray(keys) ? keys : [keys]);
  }, []);

  const originalHandleDragStart = useCallback((event: DragStartEvent) => {
    const { active } = event;
    const topic = active.data.current?.topic as ContentTopic | undefined;
    setActiveItem(topic || null);
  }, []);

  const originalHandleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      const serverTopics = currentBusiness?.topics;

      if (!serverTopics) {
        console.error(
          "Failed to update topic category: Server topics data is not available."
        );
        message.error(
          "Could not update topic category: server data unavailable."
        );
        return;
      }

      if (active?.data.current?.topic && over) {
        const targetCategoryId =
          over.id === "Uncategorized" ? null : over.id.toString();

        const draggedTopicFromDisplay = active.data.current
          .topic as ContentTopic;

        const draggedTopicIndexInServerTopics = serverTopics.findIndex(
          (t: ContentTopic) =>
            t.title === draggedTopicFromDisplay.title &&
            t.body === draggedTopicFromDisplay.body &&
            t.category_id === draggedTopicFromDisplay.category_id
        );

        if (draggedTopicIndexInServerTopics !== -1) {
          const topicToUpdateInServerList =
            serverTopics[draggedTopicIndexInServerTopics];

          if (topicToUpdateInServerList.category_id !== targetCategoryId) {
            const updatedTopicInServerList = {
              ...topicToUpdateInServerList,
              category_id: targetCategoryId,
            };
            const newServerTopicsList = serverTopics.map(
              (topic: ContentTopic, index: number) =>
                index === draggedTopicIndexInServerTopics
                  ? updatedTopicInServerList
                  : topic
            );
            if (currentBusiness?.id) {
              dispatch(
                updateBusinessDetails({ topics: newServerTopicsList } as any)
              )
                .unwrap()
                .catch((error: any) => {
                  console.error(
                    "Failed to save topic category change (original error may have been 'Failed to save topic order'):",
                    error
                  );
                  message.error("Failed to save topic category change.");
                });
            }
          }
        } else {
          console.error(
            "Dragged topic not found in server topics list. Displayed topics might be out of sync or matching failed."
          );
          message.error(
            "Could not update topic category: Topic not found in server data."
          );
        }
      }
    },
    [dispatch, currentBusiness]
  );

  const originalHandleDragCancel = useCallback(() => {
    // setActiveItem(null);
  }, []);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      originalHandleDragStart(event);
    },
    [originalHandleDragStart]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveItem(null);
      originalHandleDragEnd(event);
    },
    [originalHandleDragEnd]
  );

  const handleDragCancel = useCallback(
    (_event: DragCancelEvent) => {
      setActiveItem(null);
      originalHandleDragCancel();
    },
    [originalHandleDragCancel]
  );

  return {
    activeItem,
    activeCategoryKeys,
    handleCollapseChange,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  };
};
